import React, {
    useEffect,
    useMemo,
    useState
} from 'react';

import gd from '../../../../assets/images/google-drive.png';
import ic from '../../../../assets/images/icloud.png';
import img from '../../../../assets/images/system-run.png';
import {
    ActivityIndicator,
    Img,
    Title,
    TouchableOpacity,
    View
} from '../../../../common';
import { MultiLineTextPlaceholder } from '../../../../common/TextPlaceholder';
import { useIsLastBackupFailed } from '../../../../hooks/useIsLastBackupsFailed';
import { useLocale } from '../../../../hooks/useLocale';
import { COLORS } from '../../../../styles/colors';
import { getDeviceNameFromFile } from '../google/googleDrive';
import { formatDateSmart } from '../util';
import { styles } from './styles';

export const LastBackupInfo = ({
    enable = false,
    lastBackup,
    size = 0,
    onRefresh,
    backupProcessing,
    infoProcessing,
    isGoogleDrive = true,
    fileName,
    redOnError = true,
    logo = true,
}) => {
    const locale = useLocale();
    const backupError = useIsLastBackupFailed();
    const [ deviceName, setDeviceName ] = useState('');

    useEffect(() => {
        if (!fileName) {
            return;
        }
        getDeviceNameFromFile(fileName)
            .then(setDeviceName)
            .catch(console.error);
    }, [ fileName ]);

    const sizeMb = useMemo(() => {
        try {
            return (size / 1024 / 1024).toFixed(1);
        } catch (e) {
            return 0;
        }
    }, [ size ]);

    const infoProcess = useMemo(() => {
        if (lastBackup) {
            return (
                <View style={[ styles.textWrapper ]}>
                    <Title
                        style={[
                            styles.text,
                            backupError && redOnError
                                ? {
                                    color: COLORS.ERROR,
                                }
                                : {
                                    color: COLORS.STANDARD_GRAY,
                                },
                        ]}
                        numberOfLines={0}
                    >
                        {Object.R('titles.googleDriveBackupInfoTitle')}
                    </Title>
                    <Title
                        style={[
                            styles.text,
                            styles.bold,
                            backupError && redOnError
                                ? {
                                    color: COLORS.ERROR,
                                }
                                : {
                                    color: COLORS.STANDARD_GRAY,
                                },
                        ]}
                        numberOfLines={0}
                    >
                        {sizeMb < 0.1 ? Object.R('titles.googleDriveBackupInfoWithoutSize', {
                            date: formatDateSmart(lastBackup, locale),
                            device: `${deviceName ? `, ${deviceName}` : ''}`,
                        }) : Object.R('titles.googleDriveBackupInfo', {
                            date: formatDateSmart(lastBackup, locale),
                            size: sizeMb,
                            device: `${deviceName ? `, ${deviceName}` : ''}`,
                        })}
                    </Title>
                </View>
            );
        }

        return infoProcessing ? (
            <MultiLineTextPlaceholder
                lines={2}
                lineHeight={14}
                lineSpacing={3}
                style={{
                    marginRight: 16,
                }}
            />
        ) : (
            <Title
                style={[ styles.text ]}
                numberOfLines={0}
            >
                {Object.R(isGoogleDrive ? 'titles.googleDriveNoBackupInfo' : 'titles.iCloudNoBackupInfo')}
            </Title>
        );
    }, [ infoProcessing,
        lastBackup,
        backupError,
        redOnError,
        deviceName ]);

    return enable ? (
        <View style={[ styles.container ]}>
            {!!logo && <Img
                source={isGoogleDrive ? gd : ic}
                style={[ styles.image ]}
            />}
            <View style={[ styles.item ]}>
                {infoProcess}
                {!!backupProcessing && <ActivityIndicator size={'small'}/>}
                {!!onRefresh && !backupProcessing && (
                    <View style={[ styles.refreshItem ]}>
                        <TouchableOpacity
                            onPress={onRefresh}
                            accessibilityLabel="google_drive_backup"
                        >
                            <Img
                                source={img}
                                style={{
                                    width: 36,
                                    height: 36,
                                }}
                            />
                        </TouchableOpacity>
                    </View>
                )}
            </View>
        </View>
    ) : null;
};

